import type { TradeTicker } from "@cede/types";
import { API_ENDPOINT, EXCHANGES } from "@cede/utils";
import { isPerpsExchange } from "./config";
import { useApi } from "@/hooks/core/useApi";
import { useBitgetMarkPriceWS } from "@/hooks/exchanges/direct-ws/bitget/useBitgetMarkPriceWS";
import { useCryptocomMarkPriceWS } from "@/hooks/exchanges/direct-ws/cryptocom/useCryptocomMarkPriceWS";
import { useHyperliquidMarkPriceWS } from "@cede/trading-data";
import { useQuery } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

interface UseCexPerpsMarkPriceParams {
  exchangeId: string | undefined;
  pairSymbol: string | undefined;
  enabled?: boolean;
}

const CEX_PERPS_MARK_PRICE_QUERY_KEY = "cex-perps-mark-price";

/** Exchanges that use direct WebSocket for mark price instead of REST polling */
const WS_MARK_PRICE_EXCHANGES = new Set<string>([EXCHANGES.CRYPTOCOM, EXCHANGES.BITGET, EXCHANGES.HYPERLIQUID]);

/**
 * Hook to fetch mark price for perpetual markets.
 * Mark price is used to calculate unrealized PnL for perps positions.
 *
 * Crypto.com uses a direct WebSocket subscription for real-time mark price.
 * Hyperliquid uses the `activeAssetCtx` WebSocket subscription for real-time mark price.
 * Other exchanges use REST API polling via the ticker endpoint.
 */
export const useCexPerpsMarkPrice = ({ exchangeId, pairSymbol, enabled = true }: UseCexPerpsMarkPriceParams) => {
  const apiHelper = useApi();

  const isCryptocom = exchangeId === EXCHANGES.CRYPTOCOM;
  const isHyperliquid = exchangeId === EXCHANGES.HYPERLIQUID;
  const isBitget = exchangeId === EXCHANGES.BITGET;
  const usesWs = exchangeId != null && WS_MARK_PRICE_EXCHANGES.has(exchangeId);

  // Crypto.com: real-time mark price via WebSocket
  const {
    markPrice: cryptocomMarkPrice,
    isConnected: cryptocomConnected,
    error: cryptocomError,
  } = useCryptocomMarkPriceWS({
    pairSymbol,
    enabled: enabled && isCryptocom && !!pairSymbol,
  });

  // Hyperliquid: real-time mark price via activeAssetCtx WebSocket
  const { markPrice: hlMarkPrice, isConnected: hlConnected } = useHyperliquidMarkPriceWS({
    coin: pairSymbol,
    enabled: enabled && isHyperliquid && !!pairSymbol,
  });

  // Bitget: real-time mark price via WebSocket
  const {
    markPrice: bitgetMarkPrice,
    isConnected: bitgetConnected,
    error: bitgetError,
  } = useBitgetMarkPriceWS({
    pairSymbol,
    enabled: enabled && isBitget && !!pairSymbol,
  });

  // Other exchanges: REST API polling
  const { data, isLoading, error } = useQuery({
    queryKey: [CEX_PERPS_MARK_PRICE_QUERY_KEY, exchangeId, pairSymbol],
    queryFn: async () => {
      if (!exchangeId || !pairSymbol) return null;

      try {
        const ticker: TradeTicker = await apiHelper.getOrThrow(API_ENDPOINT.HUB_TRADE_TICKER, {
          queryParams: { exchangeId, pairSymbol },
        });
        return ticker?.markPrice;
      } catch (err) {
        enqueueSnackbar("Error fetching mark price", { variant: "error" });
        throw err;
      }
    },
    enabled: enabled && !usesWs && !!exchangeId && !!pairSymbol && isPerpsExchange(exchangeId),
    refetchInterval: 5000,
    staleTime: 2000,
    refetchOnWindowFocus: true,
  });

  if (isCryptocom) {
    return {
      markPrice: cryptocomMarkPrice,
      isLoading: !cryptocomConnected && !cryptocomMarkPrice,
      error: cryptocomError,
    };
  }

  if (isBitget) {
    const bitgetEnabled = enabled && !!pairSymbol;
    return {
      markPrice: bitgetMarkPrice,
      isLoading: bitgetEnabled && !bitgetConnected && !bitgetMarkPrice,
      error: bitgetError,
    };
  }

  if (isHyperliquid) {
    return {
      markPrice: hlMarkPrice ?? undefined,
      isLoading: !hlConnected && !hlMarkPrice,
      error: null,
    };
  }

  return {
    markPrice: data ?? undefined,
    isLoading,
    error,
  };
};
